import { Suspense, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useChartTheme } from '@/lib/charts/use-chart-theme';
import { cn } from '@/lib/utils';
import { EscenaDeMomentoDiferida } from '../animaciones/cargar-escena';
import { logoDePrixia } from '../animaciones/elegir-logo';
import {
  DURACION_DE_MOMENTO,
  TITULO_DE_PLAN_CONVERTIDO,
  type Momento,
} from '../animaciones/momentos';
import { anotarMomentoAbierto } from '../momento-abierto';
import { MARGEN_DE_SEGURIDAD_MS, PLAZO_DE_CARGA_MS } from '../animaciones/tiempos-de-apertura';

const PANEL = 'h-[240px] w-[300px] overflow-hidden rounded-2xl border bg-card shadow-xl';

/**
 * Los momentos 3 y 5 (spec 344) encima de la pantalla. Se ve recién con la escena lista,
 * no recibe clics y se cierra solo: si el 3D no llega a tiempo, falla o la pestaña está
 * oculta, se va sin dejar rastro y la pantalla sigue como siempre.
 */
export function CapaDeMomento({
  momento,
  className,
  alListo,
  alNoSeVio,
  alCerrar,
}: {
  momento: Momento;
  /** Dónde va: la pone quien la usa. */
  className: string;
  /** La escena arrancó: acá se marca el momento como visto. */
  alListo?: () => void;
  /** La escena falló o venció el plazo de carga sin llegar a verse. */
  alNoSeVio?: () => void;
  alCerrar: () => void;
}) {
  const { oscuro } = useChartTheme();
  // Se fija al abrir: si cambiara, la escena se rehace y la animación vuelve a empezar.
  const [fondo] = useState<'claro' | 'oscuro'>(oscuro ? 'oscuro' : 'claro');
  const [lista, setLista] = useState(false);
  const cerrar = useRef(alCerrar);
  cerrar.current = alCerrar;
  const noSeVio = useRef(alNoSeVio);
  noSeVio.current = alNoSeVio;
  const vista = useRef(false);
  const abandonar = () => {
    if (!vista.current) noSeVio.current?.();
    cerrar.current();
  };

  // Mientras la capa existe, ningún tutorial ni bienvenida se abre encima (momento-abierto.ts).
  useEffect(() => anotarMomentoAbierto(), []);

  useEffect(() => {
    const plazo = lista ? DURACION_DE_MOMENTO[momento] + MARGEN_DE_SEGURIDAD_MS : PLAZO_DE_CARGA_MS;
    const id = window.setTimeout(() => {
      if (!lista && !vista.current) noSeVio.current?.();
      cerrar.current();
    }, plazo);
    return () => window.clearTimeout(id);
  }, [lista, momento]);

  return (
    <>
      <motion.div
        data-capa-de-momento=""
        aria-hidden="true"
        className={cn('pointer-events-none', momento !== 'ingreso' && PANEL, className)}
        initial={{ opacity: 0 }}
        animate={{ opacity: lista ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Suspense fallback={null}>
          <EscenaDeMomentoDiferida
            momento={momento}
            logo={logoDePrixia()}
            fondo={fondo}
            alListo={() => {
              vista.current = true;
              setLista(true);
              alListo?.();
            }}
            alTerminar={() => cerrar.current()}
            alFallar={abandonar}
          />
        </Suspense>
      </motion.div>
      {/* El contenedor de arriba está oculto a los lectores: el título se anuncia desde acá. */}
      {momento === 'plan-convertido' && lista && (
        <p role="status" className="sr-only">
          {TITULO_DE_PLAN_CONVERTIDO}
        </p>
      )}
    </>
  );
}
